import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../landing-pages/home-two/components/nav";
import { MTN_YELLOW } from "../../constants/Colors";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import {
  DAY_MS,
  DOSE_SLOTS,
  buildTray,
  dayProgress,
  hasSlotPassed,
  isSlotOpen,
  pickDayTablet,
  type DoseItem,
  type SlotState,
} from "../../components/games/dose/dose-config";

/**
 * Throwaway spike for Dose Dash, the second game. Like the Kente weave spike,
 * this exists to answer the one question a design document cannot: does the
 * day cycle feel expensive or cheap on a real phone.
 *
 * Not linked from the hub, and not the game — no scoring, no rounds, no
 * difficulty curve. A day runs, three slots come due, you catch or miss them.
 *
 * The state machine lives here and the scene is a dumb renderer, the same
 * split as Kae Kente: both read the same wall clock, the page to decide which
 * slot is open, the scene to move the sun smoothly.
 */
const DoseDay = lazy(() => import("../../components/games/dose/DoseDay"));

/** The page re-derives slot states on a timer rather than per frame: this is
 *  a decision about game state, not an animation, and 10 a second is far
 *  finer than a thumb. */
const TICK_MS = 100;

export default function DoseSpike() {
  useHideEbanqoWidget("dose-spike");

  const [startedAtMs, setStartedAtMs] = useState(() => performance.now());
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [slotStates, setSlotStates] = useState<Record<string, SlotState>>({});
  /** Which tablet the day asked for — held in mind while the day runs. */
  const [todaysTablet, setTodaysTablet] = useState<DoseItem>(() => pickDayTablet());
  /** The tray, reshuffled every time a slot opens so neither the item nor its
   *  position can become muscle memory. */
  const [tray, setTray] = useState<DoseItem[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  /** Which slot the tray currently belongs to. */
  const openSlotKey = useRef<string | null>(null);

  const start = useCallback(() => {
    const tablet = pickDayTablet();
    setTodaysTablet(tablet);
    setTray([]);
    setFlash(null);
    openSlotKey.current = null;
    setStartedAtMs(performance.now());
    setSlotStates({});
    setProgress(0);
    setRunning(true);
  }, []);

  useEffect(() => {
    if (!running) return;
    const tablet = todaysTablet;

    timer.current = window.setInterval(() => {
      const now = dayProgress(startedAtMs, performance.now());
      setProgress(now);

      setSlotStates((current) => {
        const next = { ...current };
        for (const slot of DOSE_SLOTS) {
          const state = next[slot.key] ?? "pending";
          // Taken, missed and wrong are all final; nothing reopens a slot.
          if (state !== "pending" && state !== "open") continue;
          if (isSlotOpen(slot, now)) {
            next[slot.key] = "open";
            if (openSlotKey.current !== slot.key) {
              openSlotKey.current = slot.key;
              setTray(buildTray(tablet));
            }
          } else if (hasSlotPassed(slot, now)) {
            next[slot.key] = "missed";
            if (openSlotKey.current === slot.key) {
              openSlotKey.current = null;
              setTray([]);
            }
          }
        }
        return next;
      });

      if (now >= 1) setRunning(false);
    }, TICK_MS);

    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
  }, [running, startedAtMs, todaysTablet]);

  /** Tapping an item answers whichever slot is currently open. The right
   *  tablet fills it; anything else spends it. */
  const tapItem = (item: DoseItem) => {
    const slotKey = openSlotKey.current;
    if (!slotKey) {
      setFlash("Nothing due yet");
      window.setTimeout(() => setFlash(null), 900);
      return;
    }

    const correct = item.key === todaysTablet.key;
    setSlotStates((current) => ({
      ...current,
      [slotKey]: correct ? "taken" : "wrong",
    }));
    setFlash(correct ? "Caught it" : `That was the ${item.label.toLowerCase()}`);
    window.setTimeout(() => setFlash(null), 1200);
    openSlotKey.current = null;
    setTray([]);
  };

  const taken = Object.values(slotStates).filter((s) => s === "taken").length;
  const missed = Object.values(slotStates).filter((s) => s === "missed").length;
  const wrong = Object.values(slotStates).filter((s) => s === "wrong").length;
  const finished = !running && progress >= 1;

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-950 text-white">
      <div className="text-black">
        <Nav />
      </div>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-4 py-5">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h1 className="m-0 text-lg font-bold">Dose Dash — spike</h1>
            <p className="m-0 text-[11px] text-white/40">
              Day cycle only. No scoring, no rounds.
            </p>
          </div>
          <Link to="/play" className="text-xs text-white/50 underline">
            Games
          </Link>
        </div>

        <div className="mt-4 h-[38vh] min-h-[220px] w-full overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-white/40">
                Loading the day…
              </div>
            }
          >
            <DoseDay
              startedAtMs={startedAtMs}
              dayMs={DAY_MS}
              running={running}
              slotPoints={DOSE_SLOTS.map((slot) => slot.at)}
              // The spike tracks state by slot name; the scene keys by index
              // (the game's slots have no names), so translate on the way in.
              slotStates={Object.fromEntries(
                DOSE_SLOTS.map((slot, index) => [
                  String(index),
                  slotStates[slot.key] ?? "pending",
                ]),
              )}
            />
          </Suspense>
        </div>

        {/* Progress through the day, so the window is legible without staring
            at the sun. */}
        <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full transition-[width] duration-100"
            style={{
              width: `${Math.round(progress * 100)}%`,
              backgroundColor: MTN_YELLOW,
            }}
          />
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-white/5 px-3 py-2">
          <span className="text-[11px] uppercase tracking-wide text-white/40">
            Today&apos;s tablet
          </span>
          <span
            className="h-4 w-4 rounded-full"
            style={{ backgroundColor: todaysTablet.colour }}
            aria-hidden
          />
          <span className="text-xs font-semibold">{todaysTablet.label}</span>
        </div>

        <p className="mt-3 text-center text-xs text-white/60" aria-live="polite">
          {!running && progress === 0
            ? "A day passes. Catch each slot while it is lit."
            : running
              ? "Catch the lit slot"
              : `Day over — ${taken} caught, ${missed} missed, ${wrong} wrong item`}
        </p>

        {/* Slots are status now, not controls — the tray below is what you
            tap. */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          {DOSE_SLOTS.map((slot) => {
            const state = slotStates[slot.key] ?? "pending";
            return (
              <div
                key={slot.key}
                aria-label={`${slot.label} — ${state}`}
                className={[
                  "rounded-xl px-3 py-2 text-center text-xs font-semibold",
                  state === "open"
                    ? "text-black"
                    : state === "taken"
                      ? "bg-green-800 text-white"
                      : state === "wrong"
                        ? "bg-red-800 text-white/85"
                        : state === "missed"
                          ? "bg-red-950 text-white/60"
                          : "bg-white/10 text-white/40",
                ].join(" ")}
                style={state === "open" ? { backgroundColor: MTN_YELLOW } : undefined}
              >
                {slot.label}
                <span className="mt-0.5 block text-[10px] font-normal opacity-75">
                  {state === "pending"
                    ? "not yet"
                    : state === "open"
                      ? "now"
                      : state === "wrong"
                        ? "wrong item"
                        : state}
                </span>
              </div>
            );
          })}
        </div>

        {/* The tray. Two tablets and two everyday things: the work is holding
            today's tablet in mind and not grabbing the nearest item. */}
        <div className="mt-3 min-h-[92px]">
          {tray.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {tray.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => tapItem(item)}
                  aria-label={item.label}
                  className="flex flex-col items-center gap-1.5 rounded-xl bg-white/10 px-2 py-2.5 text-[10px] font-semibold text-white/85 active:bg-white/20"
                >
                  <span
                    className={[
                      "h-7 w-7 shrink-0",
                      item.kind === "tablet" ? "rounded-full" : "rounded-md",
                    ].join(" ")}
                    style={{ backgroundColor: item.colour }}
                    aria-hidden
                  />
                  {item.label}
                </button>
              ))}
            </div>
          ) : (
            <p className="m-0 pt-6 text-center text-[11px] text-white/30">
              The tray appears when a slot is due.
            </p>
          )}
        </div>

        <p className="mt-2 h-4 text-center text-xs text-white/70" aria-live="polite">
          {flash ?? ""}
        </p>

        <button
          type="button"
          onClick={start}
          className="mt-4 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:brightness-95"
          style={{ backgroundColor: MTN_YELLOW }}
        >
          {finished ? "Run the day again" : running ? "Restart the day" : "Start the day"}
        </button>

        <p className="mt-4 text-[10px] leading-relaxed text-white/30">
          A day takes {Math.round(DAY_MS / 1000)}s. Generic tablets, not
          medicines — a game, not a reminder. Water and bread are not
          mistakes; they are just not what the slot asked for.
        </p>
      </main>
    </div>
  );
}
